import React, { useState } from 'react'
import { StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Button, Icon, Text, TextInput } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { formatShortDate } from '@utils'
import {
  useEventEmailCampaigns,
  useEventInvitations,
  useOrganizerAttendees,
  useSendEventEmail,
  useSendEventTickets,
  type EmailRecipientFilter,
  type SendEventEmailResult,
  type SendEventTicketsResult,
} from '@hooks'
import { OptionChips } from './OptionChips'

const FILTERS: readonly EmailRecipientFilter[] = ['all', 'present', 'absent', 'invited']

const SUBJECT_MAX = 200
const MESSAGE_MAX = 10_000

function Checkbox({
  label,
  checked,
  disabled,
  onPress,
  testID,
}: {
  label: string
  checked: boolean
  disabled?: boolean
  onPress: () => void
  testID?: string
}) {
  const theme = useTheme()
  const styles = getCheckboxStyles(theme)
  return (
    <TouchableOpacity
      accessibilityRole="checkbox"
      accessibilityState={{ checked, disabled }}
      aria-checked={checked}
      aria-disabled={disabled}
      disabled={disabled}
      testID={testID}
      style={[styles.row, disabled && styles.disabled]}
      onPress={onPress}
    >
      <View style={[styles.box, checked && styles.boxChecked]}>
        {checked ? <Icon name="check" size={12} color={theme.colors.white} /> : null}
      </View>
      <Text type="bodySmall" color={theme.colors.secondary} style={styles.label}>
        {label}
      </Text>
    </TouchableOpacity>
  )
}

const getCheckboxStyles = (theme: Theme) =>
  StyleSheet.create({
    row: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.sm },
    disabled: { opacity: 0.4 },
    box: {
      width: 20,
      height: 20,
      borderRadius: theme.border.small,
      borderWidth: 1,
      borderColor: theme.colors.grey,
      alignItems: 'center',
      justifyContent: 'center',
    },
    boxChecked: { backgroundColor: theme.colors.primary, borderColor: theme.colors.primary },
    label: { flex: 1 },
  })

/**
 * "Emailing simple" from the CRM + the "notification seulement" variant
 * (same screen, a checkbox turns the email off and keeps only the in-app
 * notification — e.g. a last-minute room change).
 */
export function EmailingPanel({ eventId }: { eventId: string }) {
  const theme = useTheme()
  const { t, currentLanguage } = useLanguage()
  const styles = getStyles(theme)

  const attendeeCounts = useOrganizerAttendees(eventId, '', 'all')
  const invitations = useEventInvitations(eventId)
  const history = useEventEmailCampaigns(eventId)
  const send = useSendEventEmail()
  const sendTickets = useSendEventTickets()
  const [ticketsResult, setTicketsResult] = useState<SendEventTicketsResult | null>(null)

  const [recipients, setRecipients] = useState<EmailRecipientFilter>('all')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [alsoNotification, setAlsoNotification] = useState(false)
  const [notificationOnly, setNotificationOnly] = useState(false)
  const [result, setResult] = useState<SendEventEmailResult | null>(null)

  const totals = attendeeCounts.data?.metadata.totals
  const countsByFilter: Record<EmailRecipientFilter, number | undefined> = {
    all: totals?.total,
    present: totals?.checkedIn,
    absent: totals !== undefined ? totals.total - totals.checkedIn : undefined,
    invited:
      invitations.data?.metadata !== undefined
        ? invitations.data.metadata.count - invitations.data.metadata.accepted
        : undefined,
  }

  // Invited-but-not-registered people have no KeepTouch account: no in-app
  // notification is possible for them, so the notification options make no
  // sense for that filter — the picker resets them instead of hiding a broken option.
  const canNotify = recipients !== 'invited'

  const canSend =
    subject.trim().length > 0 &&
    message.trim().length > 0 &&
    !send.isPending &&
    (!notificationOnly || canNotify)

  const submit = () => {
    send.mutate(
      {
        eventId,
        subject: subject.trim(),
        message: message.trim(),
        recipients,
        alsoNotification: canNotify && (alsoNotification || notificationOnly),
        notificationOnly: canNotify && notificationOnly,
      },
      {
        onSuccess: (data) => {
          setResult(data)
          setSubject('')
          setMessage('')
          setAlsoNotification(false)
          setNotificationOnly(false)
        },
      },
    )
  }

  return (
    <View style={styles.panel}>
      <Text type="subtitleBold">{t('events.manage.emailing.title')}</Text>
      <Text type="bodySmall" color={theme.colors.greyBlue}>
        {t('events.manage.emailing.hint')}
      </Text>

      <OptionChips
        testID="emailing-recipients"
        label={t('events.manage.emailing.recipients')}
        options={FILTERS.map((value) => ({
          value,
          label: t(`events.manage.emailing.recipients${capitalize(value)}`, {
            count: countsByFilter[value] ?? 0,
          }),
        }))}
        value={recipients}
        onChange={(value) => {
          if (!value) return
          setRecipients(value)
          if (value === 'invited') {
            setAlsoNotification(false)
            setNotificationOnly(false)
          }
        }}
      />

      <TextInput
        testID="emailing-subject"
        title={t('events.manage.emailing.subject')}
        placeholder={t('events.manage.emailing.subjectPlaceholder')}
        value={subject}
        onChangeText={(v) => setSubject(v.slice(0, SUBJECT_MAX))}
        maxLength={SUBJECT_MAX}
      />
      <TextInput
        testID="emailing-message"
        title={t('events.manage.emailing.message')}
        placeholder={t('events.manage.emailing.messagePlaceholder')}
        value={message}
        onChangeText={(v) => setMessage(v.slice(0, MESSAGE_MAX))}
        isMultiline
        multiline
        numberOfLines={5}
        maxLength={MESSAGE_MAX}
      />

      {canNotify ? (
        <View style={styles.checkboxes}>
          <Checkbox
            testID="emailing-also-notification"
            label={t('events.manage.emailing.alsoNotification')}
            checked={alsoNotification || notificationOnly}
            disabled={notificationOnly}
            onPress={() => setAlsoNotification((v) => !v)}
          />
          <Checkbox
            testID="emailing-notification-only"
            label={t('events.manage.emailing.notificationOnly')}
            checked={notificationOnly}
            onPress={() => setNotificationOnly((v) => !v)}
          />
        </View>
      ) : null}

      {subject.trim() || message.trim() ? (
        <View style={styles.preview} testID="emailing-preview">
          <Text type="bodyExtraSmallBold" color={theme.colors.greyBlue}>
            {t('events.manage.emailing.preview')}
          </Text>
          <Text type="bodySmallBold" color={theme.colors.secondary}>
            {subject.trim()}
          </Text>
          <Text type="bodySmall" color={theme.colors.secondary}>
            {message.trim()}
          </Text>
        </View>
      ) : null}

      <Button
        testID="emailing-send"
        title={
          notificationOnly && canNotify
            ? t('events.manage.emailing.sendNotificationOnly')
            : t('events.manage.emailing.send')
        }
        iconName="send"
        disabled={!canSend}
        onPress={submit}
        containerStyle={styles.fullWidth}
      />

      {result ? (
        <View style={styles.result}>
          <Text type="bodySmallBold" color={theme.colors.green}>
            {t(
              result.notificationSent && !result.emailSent
                ? 'events.manage.emailing.resultNotificationOnly'
                : 'events.manage.emailing.result',
              { count: result.recipientCount },
            )}
          </Text>
        </View>
      ) : null}

      <View style={styles.ticketsBlock}>
        <Text type="subtitleBold">{t('events.manage.tickets.title')}</Text>
        <Text type="bodySmall" color={theme.colors.greyBlue}>
          {t('events.manage.tickets.hint')}
        </Text>
        <Button
          title={
            sendTickets.isPending
              ? t('events.manage.tickets.sending')
              : t('events.manage.tickets.sendAll', { count: totals?.total ?? 0 })
          }
          testID="send-all-tickets"
          iconName="send"
          unfilled
          disabled={sendTickets.isPending || (totals?.total ?? 0) === 0}
          onPress={() =>
            sendTickets.mutate(
              { eventId, recipients: 'all' },
              { onSuccess: setTicketsResult },
            )
          }
          containerStyle={styles.fullWidth}
        />
        {ticketsResult ? (
          <View style={styles.result} testID="tickets-result">
            <Text type="bodySmallBold" color={theme.colors.green}>
              {t('events.manage.tickets.result', { count: ticketsResult.recipientCount })}
            </Text>
          </View>
        ) : null}
      </View>

      <View style={styles.history}>
        <Text type="bodySmallBold" color={theme.colors.secondary}>
          {t('events.manage.emailing.history')}
        </Text>
        {(history.data?.data.length ?? 0) === 0 ? (
          <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
            {t('events.manage.emailing.historyEmpty')}
          </Text>
        ) : (
          history.data?.data.map((row) => (
            <View key={row.id} style={styles.historyRow}>
              <View style={styles.historyMain}>
                <Text type="bodyExtraSmallBold" numberOfLines={1} style={styles.historySubject}>
                  {row.subject}
                </Text>
                <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
                  {t('events.manage.emailing.historyRecipients', {
                    filter: t(`events.manage.emailing.filter.${row.recipientFilter}`),
                    count: row.recipientCount,
                  })}
                </Text>
              </View>
              <View style={styles.historyBadges}>
                <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
                  {formatShortDate(row.sentAt, currentLanguage)}
                </Text>
                <Text
                  type="bodyExtraSmallBold"
                  color={
                    row.kind === 'tickets'
                      ? theme.colors.primary
                      : row.notificationOnly
                        ? theme.colors.purple
                        : theme.colors.primary
                  }
                >
                  {row.kind === 'tickets'
                    ? t('events.manage.emailing.historyTicketBadge')
                    : row.notificationOnly
                      ? t('events.manage.emailing.historyNotificationBadge')
                      : t('events.manage.emailing.historyEmailBadge')}
                  {row.alsoNotification && !row.notificationOnly
                    ? ` ${t('events.manage.emailing.historyAlsoNotification')}`
                    : ''}
                </Text>
              </View>
            </View>
          ))
        )}
      </View>
    </View>
  )
}

function capitalize<T extends string>(value: T): string {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    panel: {
      backgroundColor: theme.colors.white,
      borderRadius: theme.border.jumbo,
      borderWidth: 1,
      borderColor: theme.colors.grey,
      padding: theme.spacing.xl,
      gap: theme.spacing.md,
    },
    checkboxes: { gap: theme.spacing.sm },
    ticketsBlock: {
      gap: theme.spacing.sm,
      borderTopWidth: 1,
      borderTopColor: theme.colors.grey,
      paddingTop: theme.spacing.md,
    },
    preview: {
      gap: theme.spacing.xs,
      padding: theme.spacing.md,
      borderRadius: theme.border.medium,
      backgroundColor: theme.colors.sidebar,
    },
    fullWidth: { width: '100%' },
    result: {
      padding: theme.spacing.md,
      borderRadius: theme.border.medium,
      backgroundColor: theme.colors.successSoft,
    },
    history: {
      gap: theme.spacing.xs,
      borderTopWidth: 1,
      borderTopColor: theme.colors.grey,
      paddingTop: theme.spacing.md,
    },
    historyRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      gap: theme.spacing.sm,
      paddingVertical: theme.spacing.xs,
    },
    historyMain: { flex: 1, minWidth: 0, gap: 2 },
    historySubject: {},
    historyBadges: { alignItems: 'flex-end', gap: 2 },
  })
